import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './app/auth';
import { ProtectedRoute } from './app/auth';
import { PublicLayout, AppLayout } from './app/layouts';
import { Download, X } from 'lucide-react';
import {
  setupPwaInstallPrompt,
  triggerPwaInstall,
  dismissPwaInstallPrompt,
  isStandalonePwa,
} from './utils/pwaInstall';

// Auth
import { LoginPage } from './modules/auth/pages';

// Test
import { TestPage } from './modules/test/pages';

// POS
import { POSPage } from './modules/pos/pages';

// Clientes (solo ClienteCreditoPage, usado en /vendedor/creditos/:clienteId)
import { ClienteCreditoPage } from './modules/clientes/pages';

// Caja
import { CajaVendedorPage, CajaLoteriaPage, CierreLoteriasPage, RecargasPage } from './modules/caja/pages';

// Vendedor
import { MisVentasPage, VendedorCreditosPage, VendedorListasPage, ValidacionPremiosPage, VendedorConfigTicketPage, VendedorGanadoresPage } from './modules/vendedor/pages';

// Local
import {
  LocalDashboardPage,
  LocalesPage,
  VendedoresPage,
  ReglasPage,
  LocalReportesPage,
  LocalListasPage,
  LocalSaldoPage,
  LocalRestringidosPage,
  LocalVendedoresSaldosPage,
  LocalConfigTicketPage,
  LocalValidacionSinpePage,
  LocalGanadoresPage,
} from './modules/local/pages';

// Banca
import {
  BancaDashboardPage,
  SorteosPage,
  RestringidosPage,
  NumerosGanadoresPage,
  PremiosPage,
  ListasPage,
  BancaReportesPage,
  BancaVendedoresPage,
  SaldosPage,
  BancaLocalesSorteosPage,
  AuditoriaPage,
} from './modules/banca/pages';
import { AnaliticaDashboard } from './modules/analitica/pages/AnaliticaDashboard';

// Admin
import {
  AdminDashboardPage,
  BancasPage,
  UsuariosPage,
  SuscripcionesPage,
  PagosPage,
  SeguridadPage
} from './modules/admin/pages';

// Redirección a la ruta base según el rol del usuario autenticado
const RoleBasedRedirect: React.FC = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const redirects: Record<string, string> = {
    ADMIN: '/admin',
    BANCA: '/banca',
    LOCAL: '/local',
    VENDEDOR_CON_CAJA: '/vendedor/pos',
    VENDEDOR_SIN_CAJA: '/vendedor/pos',
  };
  return <Navigate to={redirects[user.role] ?? '/login'} replace />;
};

const App: React.FC = () => {
  const [mostrarInstalacionPwa, setMostrarInstalacionPwa] = useState(false);
  const visibleModalPwaRef = useRef(false);

  useEffect(() => {
    if (isStandalonePwa()) {
      return;
    }

    const unsubscribe = setupPwaInstallPrompt((available) => {
      if (visibleModalPwaRef.current === available) {
        return;
      }
      visibleModalPwaRef.current = available;
      setMostrarInstalacionPwa(available);
    });

    return unsubscribe;
  }, []);

  const instalarPwa = async () => {
    const accepted = await triggerPwaInstall();
    if (accepted) {
      visibleModalPwaRef.current = false;
      setMostrarInstalacionPwa(false);
    }
  };

  const ocultarInstalacionPwa = () => {
    dismissPwaInstallPrompt();
    visibleModalPwaRef.current = false;
    setMostrarInstalacionPwa(false);
  };

  return (
    <AuthProvider>
      {mostrarInstalacionPwa && (
        <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-6 md:max-w-sm bg-blue-600 text-white shadow-xl rounded-xl p-3 border border-blue-500">
          <div className="flex items-start gap-2">
            <Download size={18} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold">Instalar TiemPos</p>
              <p className="text-xs text-blue-100">Agrega la app al inicio para usarla como POS en Android.</p>
            </div>
            <button
              onClick={ocultarInstalacionPwa}
              className="text-blue-100 hover:text-white"
              aria-label="Ocultar instalación"
            >
              <X size={16} />
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              onClick={instalarPwa}
              className="flex-1 bg-white text-blue-700 text-sm font-semibold py-1.5 rounded-lg hover:bg-blue-50"
            >
              Instalar
            </button>
            <button
              onClick={ocultarInstalacionPwa}
              className="px-3 py-1.5 text-sm rounded-lg border border-blue-400 text-blue-100 hover:text-white"
            >
              Luego
            </button>
          </div>
        </div>
      )}
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/test" element={<TestPage />} />
          </Route>

          {/* Protected Routes */}
          <Route element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            {/* Redirect root a la ruta base del rol autenticado */}
            <Route path="/" element={<RoleBasedRedirect />} />

            {/* VENDEDOR Routes - Main entry points for vendors */}
            <Route path="/vendedor" element={<Navigate to="/vendedor/pos" replace />} />
            <Route path="/vendedor/pos" element={
              <ProtectedRoute allowedRoles={['VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA']}>
                <POSPage />
              </ProtectedRoute>
            } />
            <Route path="/vendedor/caja" element={
              <ProtectedRoute allowedRoles={['VENDEDOR_CON_CAJA']}>
                <CajaVendedorPage />
              </ProtectedRoute>
            } />
            <Route path="/vendedor/mis-ventas" element={
              <ProtectedRoute allowedRoles={['VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA']}>
                <MisVentasPage />
              </ProtectedRoute>
            } />
            <Route path="/vendedor/listas" element={
              <ProtectedRoute allowedRoles={['VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA']}>
                <VendedorListasPage />
              </ProtectedRoute>
            } />
            <Route path="/vendedor/clientes" element={<Navigate to="/vendedor/creditos" replace />} />
            <Route path="/vendedor/creditos" element={
              <ProtectedRoute allowedRoles={['VENDEDOR_CON_CAJA', 'LOCAL']}>
                <VendedorCreditosPage />
              </ProtectedRoute>
            } />
            <Route path="/vendedor/creditos/:clienteId" element={
              <ProtectedRoute allowedRoles={['VENDEDOR_CON_CAJA', 'LOCAL']}>
                <ClienteCreditoPage />
              </ProtectedRoute>
            } />
            <Route path="/vendedor/premios" element={
              <ProtectedRoute allowedRoles={['VENDEDOR_CON_CAJA']}>
                <ValidacionPremiosPage />
              </ProtectedRoute>
            } />
            <Route path="/vendedor/ticket-config" element={
              <ProtectedRoute allowedRoles={['VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA']}>
                <VendedorConfigTicketPage />
              </ProtectedRoute>
            } />
            <Route path="/vendedor/ganadores" element={
              <ProtectedRoute allowedRoles={['VENDEDOR_CON_CAJA', 'VENDEDOR_SIN_CAJA']}>
                <VendedorGanadoresPage />
              </ProtectedRoute>
            } />

            {/* Legacy Routes (redirect to vendedor paths) */}
            <Route path="/pos" element={<Navigate to="/vendedor/pos" replace />} />
            <Route path="/caja" element={<Navigate to="/vendedor/caja" replace />} />
            <Route path="/mis-ventas" element={<Navigate to="/vendedor/mis-ventas" replace />} />
            <Route path="/clientes" element={<Navigate to="/vendedor/creditos" replace />} />
            <Route path="/clientes/:id" element={<Navigate to="/vendedor/creditos" replace />} />
            <Route path="/clientes/cartera" element={<Navigate to="/vendedor/creditos" replace />} />
            <Route path="/clientes/rapidos" element={<Navigate to="/vendedor/creditos" replace />} />

            {/* Caja de Lotería - Solo LOCAL, BANCA */}
            <Route path="/caja-loteria" element={
              <ProtectedRoute allowedRoles={['LOCAL', 'BANCA']}>
                <CajaLoteriaPage />
              </ProtectedRoute>
            } />

            {/* Lotería (Inventario) - Solo LOCAL/BANCA */}
            <Route path="/loteria-caja" element={
              <ProtectedRoute allowedRoles={['LOCAL', 'BANCA']}>
                <CajaLoteriaPage />
              </ProtectedRoute>
            } />

            {/* Cierre de Loterías - Solo LOCAL */}
            <Route path="/local/cierre-loterias" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <CierreLoteriasPage />
              </ProtectedRoute>
            } />

            {/* Recargas (saldo prepago) - LOCAL y VENDEDOR_CON_CAJA */}
            <Route path="/local/recargas" element={
              <ProtectedRoute allowedRoles={['LOCAL', 'VENDEDOR_CON_CAJA']}>
                <RecargasPage />
              </ProtectedRoute>
            } />

            {/* LOCAL Routes */}
            <Route path="/local/dashboard" element={<Navigate to="/local" replace />} />
            <Route path="/local" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/local/locales" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalesPage />
              </ProtectedRoute>
            } />
            <Route path="/local/vendedores" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <VendedoresPage />
              </ProtectedRoute>
            } />
            <Route path="/local/reglas" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <ReglasPage />
              </ProtectedRoute>
            } />
            <Route path="/local/listas" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalListasPage />
              </ProtectedRoute>
            } />
            <Route path="/local/reportes" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalReportesPage />
              </ProtectedRoute>
            } />
            <Route path="/local/saldos" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalSaldoPage />
              </ProtectedRoute>
            } />
            <Route path="/local/ticket-config" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalConfigTicketPage />
              </ProtectedRoute>
            } />
            <Route path="/local/validacion-sinpe" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalValidacionSinpePage />
              </ProtectedRoute>
            } />
            <Route path="/local/ganadores" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <LocalGanadoresPage />
              </ProtectedRoute>
            } />

            <Route path="/local/caja-loteria" element={
              <ProtectedRoute allowedRoles={['LOCAL']}>
                <CajaLoteriaPage />
              </ProtectedRoute>
            } />


            {/* BANCA Routes */}
            <Route path="/banca/dashboard" element={<Navigate to="/banca" replace />} />
            <Route path="/banca" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <BancaDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/sorteos" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <SorteosPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/listas" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <ListasPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/vendedores" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <BancaVendedoresPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/premios" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <PremiosPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/restringidos" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <RestringidosPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/ganadores" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <NumerosGanadoresPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/reportes" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <BancaReportesPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/saldos" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <SaldosPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/locales-sorteos" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <BancaLocalesSorteosPage />
              </ProtectedRoute>
            } />
            <Route path="/banca/analitica" element={
              <ProtectedRoute allowedRoles={['BANCA']}>
                <AnaliticaDashboard />
              </ProtectedRoute>
            } />
            <Route path="/banca/auditoria" element={
              <ProtectedRoute allowedRoles={['BANCA', 'ADMIN']}>
                <AuditoriaPage />
              </ProtectedRoute>
            } />

            {/* ADMIN Routes */}
            <Route path="/admin/dashboard" element={<Navigate to="/admin" replace />} />
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <AdminDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/bancas" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <BancasPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/usuarios" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <UsuariosPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/suscripciones" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <SuscripcionesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/pagos" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <PagosPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/seguridad" element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <SeguridadPage />
              </ProtectedRoute>
            } />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
